import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthContext.jsx';
import { useCanAsMember, useDocumentTitle } from '@/hooks';
import { Button, PageHead, Skel, useConfirm, useToast } from '@/ui';
import { getOfferLinkTarget, updateOfferLinkTarget } from '@/lib/recepthor-api';
import { qk } from '@/lib/queryKeys';
import PlataformaAvisos from './PlataformaAvisos';
import './PlataformaConfiguracion.css';

// Where the quote request email sends the receptor. The API reads this on
// every send, so changing it here is the switch and also the rollback.
const TARGETS = [
  {
    value: 'fillout',
    label: 'Formulario de Fillout',
    hint: 'El link abre el formulario externo, como hasta ahora.',
  },
  {
    value: 'web',
    label: 'Página nativa (/oferta)',
    hint: 'El link abre la oferta dentro de Recepthor.',
  },
];

const SOURCE_LABEL = {
  setting: 'Guardado en la plataforma',
  environment: 'Por defecto del entorno',
};

function targetLabel(value) {
  return TARGETS.find((t) => t.value === value)?.label ?? value;
}

export default function PlataformaConfiguracion() {
  useDocumentTitle('Configuración · Plataforma');
  const { setBreadcrumb, setPills } = useOutletContext();
  const organizationId = useAuth()?.organizationId ?? null;
  // The API gives an owner every permission and ignores the stored array.
  const canUpdate = useCanAsMember('platform-settings:update');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  useEffect(() => {
    setBreadcrumb(['Configuración']);
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills]);

  const current = useQuery({
    queryKey: qk.offerLinkTarget(),
    queryFn: () => getOfferLinkTarget({ organizationId }),
    enabled: organizationId != null,
  });
  const saved = current.data?.target ?? null;
  const [choice, setChoice] = useState(null);
  const selected = choice ?? saved;

  const saveMut = useMutation({
    mutationFn: (target) => updateOfferLinkTarget(target, { organizationId }),
    onSuccess: (data) => {
      queryClient.setQueryData(qk.offerLinkTarget(), data);
      setChoice(null);
      toast({
        kind: 'success',
        message: `Los correos nuevos llevan a: ${targetLabel(data?.target)}.`,
      });
    },
    // Backoffice is internal use: the API's message is shown as worded.
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo guardar',
        message: e?.message ?? 'Error desconocido.',
      }),
  });

  const changed = selected != null && selected !== saved;

  const onSave = async () => {
    const ok = await confirm({
      title: 'Cambiar el destino del correo de cotización',
      body: (
        <>
          Los correos de solicitud que salgan desde ahora van a llevar a{' '}
          <b>{targetLabel(selected)}</b>. Cada servidor lo toma en hasta 30
          segundos. Los links que ya se mandaron siguen funcionando, porque cada
          uno conserva el destino con que salió.
        </>
      ),
      confirmLabel: 'Cambiar',
      confirmVariant: 'seal',
    });
    if (ok) saveMut.mutate(selected);
  };

  return (
    <div className="page reveal">
      <PageHead
        kicker="Plataforma"
        title="Configuración"
        sub={<>Ajustes de la plataforma que aplican a todos los estudios</>}
      />

      <section className="plc-section" aria-labelledby="plc-offer-link-title">
        <h2 id="plc-offer-link-title" className="plc-section__title">
          Correo de solicitud de cotización
        </h2>
        <p className="plc-section__body">
          Adónde lleva el botón &quot;Hacer oferta&quot; del correo que reciben
          los receptores.
        </p>

        {current.isPending ? (
          <div aria-busy="true">
            <Skel w={240} />
          </div>
        ) : current.isError ? (
          <p className="plc-error" role="alert">
            {current.error?.message ?? 'No pudimos leer la configuración.'}
          </p>
        ) : (
          <>
            <fieldset
              className="plc-options"
              disabled={!canUpdate || saveMut.isPending}
            >
              <legend className="plc-options__legend">Destino del link</legend>
              {TARGETS.map((t) => (
                <label key={t.value} className="plc-option">
                  <input
                    type="radio"
                    name="offer-link-target"
                    value={t.value}
                    checked={selected === t.value}
                    onChange={() => setChoice(t.value)}
                  />
                  <span className="plc-option__text">
                    <span className="plc-option__label">
                      {t.label}
                      {saved === t.value ? (
                        <span className="plc-option__current"> · actual</span>
                      ) : null}
                    </span>
                    <span className="plc-option__hint">{t.hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            <p className="plc-source">
              {SOURCE_LABEL[current.data?.source] ?? ''}
            </p>
            {canUpdate ? (
              <div className="plc-actions">
                <Button
                  variant="primary"
                  size="md"
                  disabled={!changed || saveMut.isPending}
                  onClick={onSave}
                >
                  Guardar
                </Button>
              </div>
            ) : (
              <p className="plc-source">
                Tu rol puede ver este ajuste, pero no cambiarlo.
              </p>
            )}
          </>
        )}
      </section>

      <PlataformaAvisos />
    </div>
  );
}
